import { z } from "zod";

/** Inline tags the footer note is allowed to carry. */
const ALLOWED_NOTE_TAGS = new Set(["span", "a", "b", "strong", "em", "br"]);

const NOTE_TAG_RE = /<\s*\/?\s*([a-zA-Z][a-zA-Z0-9-]*)/g;
// Attribute *names* are not entity-decoded by the HTML parser, so matching the
// literal "on…=" is sufficient here.
const EVENT_HANDLER_RE = /\son[a-z]+\s*=/i;
const ATTR_URL_RE = /\b(?:href|src)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;
const SCHEME_RE = /^([a-z][a-z0-9+.-]*):/i;

/** Rejects a `javascript:`/`data:` URL — anything React would not encode away. */
function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Reproduces what the HTML parser does to an attribute value before the
 * browser resolves it as a URL: decode character references once, then drop the
 * whitespace and control characters URL parsing ignores. Without this,
 * `href="javascript&#58;alert(1)"` reads as scheme-less to a literal check but
 * still executes once rendered.
 */
function decodeUrlValue(raw: string): string {
  const decoded = raw
    .replace(/&#x([0-9a-f]+);?/gi, (_, hex: string) =>
      String.fromCodePoint(parseInt(hex, 16))
    )
    .replace(/&#(\d+);?/g, (_, dec: string) =>
      String.fromCodePoint(parseInt(dec, 10))
    )
    .replace(/&colon;/gi, ":")
    .replace(/&tab;/gi, "\t")
    .replace(/&newline;/gi, "\n")
    .replace(/&amp;/gi, "&");
  return decoded.replace(/[\u0000-\u0020]/g, "");
}

/**
 * Allow-list rather than deny-list: a URL either carries no scheme (relative)
 * or carries http/https. A deny-list of known-bad schemes is what obfuscation
 * gets past.
 */
function isSafeUrlValue(raw: string): boolean {
  const scheme = SCHEME_RE.exec(decodeUrlValue(raw));
  if (!scheme?.[1]) return true;
  const name = scheme[1].toLowerCase();
  return name === "http" || name === "https";
}

/**
 * note_html is rendered through dangerouslySetInnerHTML, so validate it here
 * rather than at the sink: only the inline tags above, no event handlers, and
 * every href/src limited to http(s) or a relative path. This is boundary
 * validation, not sanitisation — a data refresh that trips it fails to parse
 * instead of being cleaned up.
 */
function isSafeNoteHtml(html: string): boolean {
  if (EVENT_HANDLER_RE.test(html)) return false;
  for (const match of html.matchAll(ATTR_URL_RE)) {
    if (!isSafeUrlValue(match[1] ?? match[2] ?? match[3] ?? "")) return false;
  }
  for (const match of html.matchAll(NOTE_TAG_RE)) {
    const tag = match[1];
    if (!tag || !ALLOWED_NOTE_TAGS.has(tag.toLowerCase())) return false;
  }
  return true;
}

/**
 * Shape of public/assets-next/ecommerce-stack/treemap_data.json — regenerated
 * monthly by the external build_data.py tool. Kept as a Zod schema so a
 * malformed refresh fails loudly at build time rather than rendering garbage.
 *
 * cells:     [band, tier, industry, appId, cntCurrent, cntPrevious]
 * segTotals: [band, tier, industry, segmentBrands]
 * apps:      array indexed by the numeric appId → { name, cat }
 */
export const treemapDataSchema = z.object({
  meta: z.object({
    title: z.string(),
    period: z.string(),
    total_label: z.string(),
    callout: z.string(),
    callout_url: z
      .string()
      .refine(isHttpUrl, "callout_url must be an http(s) URL"),
    note_html: z
      .string()
      .refine(
        isSafeNoteHtml,
        "note_html carries markup outside the inline allow-list"
      ),
  }),
  categoryOrder: z.array(z.string()),
  categoryColor: z.record(z.string(), z.string()),
  industries: z.array(z.object({ key: z.string(), label: z.string() })),
  apps: z.array(z.object({ name: z.string(), cat: z.string() })),
  cells: z.array(
    z.tuple([
      z.string(),
      z.string(),
      z.string(),
      z.number(),
      z.number(),
      z.number(),
    ])
  ),
  segTotals: z.array(z.tuple([z.string(), z.string(), z.string(), z.number()])),
  minSample: z.number(),
  top_growing: z.array(z.object({ name: z.string(), category: z.string() })),
  top_trending: z.array(z.object({ name: z.string(), category: z.string() })),
});

export type TreemapData = z.infer<typeof treemapDataSchema>;

/** Current filter selection. "all" means no restriction on that dimension. */
export interface Selection {
  gmv: string;
  growth: string;
  ind: string;
}

/** A single app leaf after aggregation. */
export interface AppLeaf {
  name: string;
  /** Adoption: % of cohort brands with the app installed. */
  value: number;
  /** Month-on-month change in percentage points. */
  momPp: number;
  /** Relative MoM change (%). null => new entrant (no installs last month). */
  momRel: number | null;
}

/** A category node grouping its app leaves. */
export interface CategoryNode {
  name: string;
  color: string;
  children: AppLeaf[];
}
